import { EllipsisHorizontalIcon } from "@heroicons/react/24/outline"
import { Dropdown, Tooltip } from "antd"
import type { DropdownProps } from "antd"
import type { ItemType } from "antd/es/menu/hooks/useItems"
import React, { type ReactNode } from "react"

/**
 * Cell building blocks for the table design.
 *
 * The icon group and the action menu stay separate components: whether a table
 * shows a row of direct icon controls or a menu behind an ellipsis button is a
 * product decision made per table, not inferred from its contents.
 */

/* ------------------------------------------------------------ identity -- */

/* ---------------------------------------------------------- icon button -- */

/**
 * The round icon button rows use.
 *
 * Figma `cell icon` (node 893:7483): 24px slate-100 circle holding a 10px
 * slate-900 glyph at 1.8 stroke. It lived inside `TableIdentityCell`, which
 * meant every other row affordance - copy an id, open a timeline - got
 * hand-rolled instead, usually smaller and usually without a hover state.
 *
 * The glyph is passed as a child and should draw with `currentColor`, so it
 * follows the button through hover and focus. An `<img>` cannot: its colour
 * lives in the file, not the DOM.
 */
export const TableIconButton = ({
  label,
  onClick,
  children,
}: {
  label: string
  onClick: () => void
  children: ReactNode
}) => (
  <Tooltip title={label}>
    <button
      type="button"
      className="inline-flex h-6 w-6 flex-none items-center justify-center rounded-full bg-[#f1f5f9] text-[#0f172b] transition-colors hover:bg-[#e2e8f0] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#004af5]"
      onClick={(event) => {
        // Rows can sit inside a clickable container; the button's job is its
        // own, not the container's.
        event.stopPropagation()
        onClick()
      }}
      aria-label={label}
    >
      {children}
    </button>
  </Tooltip>
)

/** The caret used to open a record. */
export const ChevronGlyph = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 4.925 8.05"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M0.9 0.9L4.025 4.025L0.9 7.15"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

/**
 * Avatar and name. There is no separate "open" chevron: where the row opens a
 * record, the name itself is the link or the row has its own action.
 */
export const TableIdentityCell = ({
  avatar,
  name,
}: {
  /** Rendered as-is, so a call site can pass its own initials avatar. */
  avatar?: ReactNode
  name: ReactNode
}) => (
  <div className="app-table-identity">
    {avatar}
    <span className="app-table-identity__name">{name}</span>
  </div>
)

/* --------------------------------------------------- action icon group -- */

export type TableAction = {
  key: string
  icon: ReactNode
  label: string
  onClick: () => void
  disabled?: boolean
}

/**
 * The icon variant: 36x36 hit boxes with a hairline divider between each.
 * Dividers come from CSS (`.app-table-actions__item + .app-table-actions__item`)
 * so hiding an action never leaves an orphaned rule behind it.
 */
export const TableActions = ({ actions }: { actions: TableAction[] }) => (
  <div className="app-table-actions">
    {actions.map((action) => (
      // aria-disabled rather than disabled: a disabled button receives no
      // pointer events, so its tooltip would vanish exactly when someone hovers
      // to find out what the greyed icon is. Wrapping it in a span instead would
      // break the `item + item` sibling rule that draws the dividers.
      <Tooltip
        key={action.key}
        title={action.label}
      >
        <button
          type="button"
          className="app-table-actions__item"
          onClick={(event) => {
            event.stopPropagation()
            if (action.disabled) return
            action.onClick()
          }}
          aria-disabled={action.disabled || undefined}
          aria-label={action.label}
        >
          {action.icon}
        </button>
      </Tooltip>
    ))}
  </div>
)

/* ------------------------------------------------------ action trigger -- */

/**
 * The row-actions button on its own, for the rows that build their menu inline
 * and so cannot hand a ready `items` array to `TableActionSelect`. Same element
 * either way, so a screen that mixes the two forms still renders one affordance.
 *
 * The design system's outlined ellipsis button (`TableRowActions`): 36px square,
 * 5px radius, slate-100 on hover. Its border matches the table's own lines
 * (1.4px slate-200, `--app-table-border` in appTable.css). It shows only the
 * icon, so `label` is its accessible name and hover title.
 */
export const TableActionTrigger = React.forwardRef<
  HTMLButtonElement,
  {
    label?: string
    disabled?: boolean
  } & React.ComponentPropsWithoutRef<"button">
>(({ label = "Select action", disabled, onClick, ...rest }, ref) => (
  <button
    {...rest}
    ref={ref}
    type="button"
    disabled={disabled}
    aria-label={label}
    title={label}
    onClick={(event) => {
      // The row itself is often clickable; opening the menu must not also
      // navigate away from the row the menu belongs to.
      event.stopPropagation()
      onClick?.(event)
    }}
    className={ACTION_TRIGGER_CLASS}
  >
    <EllipsisHorizontalIcon
      className="h-4 w-4"
      aria-hidden="true"
    />
  </button>
))
TableActionTrigger.displayName = "TableActionTrigger"

/* ----------------------------------------------------- approval status -- */

/** Request states, as the API numbers them. */
export const APPROVAL_STATUS = {
  approved: 1,
  declined: 2,
  pendingReview: 3,
} as const

/**
 * Where a request has got to in its approval chain.
 *
 * A settled request is one thing - Approved, Declined - and fits a pill. A
 * pending one is two: the state, and who it is sitting with. Four of the five
 * tables were putting both in the pill, so "Pending with Jane Doe" came out as
 * one long line that stretched the column and read as a status name. The
 * violations table already split them; this is that treatment, shared, so the
 * five stay in step.
 */
export type StatusTone =
  | "success"
  | "danger"
  | "pending"
  | "warning"
  | "neutral"

const STATUS_TONE_CLASS: Record<StatusTone, string> = {
  success: "rounded px-3 py-0.5 bg-[#C1EFDA] text-[#1F704B]",
  danger: "rounded px-3 py-0.5 bg-[#FBE4E1] text-[#EB3949]",
  pending:
    "rounded border-2 border-[#E69702] bg-[#FEE6CA] px-4 py-1 text-neutral800",
  warning: "rounded px-3 py-0.5 bg-[#FEF3E2] text-[#B54708]",
  neutral: "rounded px-3 py-0.5 bg-[#f1f5f9] text-[#62748e]",
}

/**
 * One status chip for every request table, whatever the request. Tables that drew their own
 * - bordered boxes, antd outline tags, a second copy of the label underneath - read as
 * different products side by side; this is the look the approval tables already use.
 */
export const StatusChip = ({
  tone,
  label,
  note,
  footnote,
}: {
  tone: StatusTone
  label: ReactNode
  /** A short line under the chip: who it is with, how far through approval. */
  note?: ReactNode
  footnote?: ReactNode
}) => (
  <div className="w-fit">
    <span
      className={`inline-block whitespace-nowrap font-avenir text-xs font-medium ${STATUS_TONE_CLASS[tone]}`}
    >
      {label}
    </span>
    {note ? <div className="mt-1 text-xs text-neutral500">{note}</div> : null}
    {footnote}
  </div>
)

export const ApprovalStatusCell = ({
  statusId,
  label,
  reviewStatus,
  footnote,
}: {
  /** `employee_request_status_id`. String or number - the APIs disagree. */
  statusId: string | number | null | undefined
  /** `employee_request_status`: the settled wording, "Approved" / "Declined". */
  label?: string | null
  /** `review_status`: who the request is pending with. */
  reviewStatus?: string | null
  /** An extra note under the status, e.g. a deactivated employee. */
  footnote?: ReactNode
}) => {
  const id = Number(statusId)

  if (id === APPROVAL_STATUS.pendingReview) {
    // Self-service lists send "Pending Review" as the reviewer line when nobody is named,
    // which printed the label twice.
    const note =
      reviewStatus && reviewStatus.trim().toLowerCase() !== "pending review"
        ? reviewStatus
        : null
    return (
      <StatusChip
        tone="pending"
        label="Pending Review"
        note={note}
        footnote={footnote}
      />
    )
  }

  return (
    <StatusChip
      tone={
        id === APPROVAL_STATUS.approved
          ? "success"
          : id === APPROVAL_STATUS.declined
          ? "danger"
          : "neutral"
      }
      label={label}
      footnote={footnote}
    />
  )
}

/**
 * The look shared by request tables beyond the columns: the same row height and cell text
 * colour as the leave requests table. Lists of short text rows looked cramped beside it,
 * because its rows are held open by an avatar and an action button, and some lists printed
 * cells in near-black while it used the grey. Applied to the table's wrapper.
 */
export const REQUEST_TABLE_CLASS =
  // Data rows only. antd puts a hidden measure row at the top of a scrolling table's body;
  // holding every td open gave that row the full height too, a blank band above the first row.
  "[&_.ant-table-tbody_tr.ant-table-row_td]:!h-20 [&_.ant-table-tbody_td]:!text-[#42526D]"

/* --------------------------------------------------------- action menu -- */

/**
 * Dismiss an open menu as soon as anything under it scrolls.
 *
 * The menu is portalled to `<body>`, which puts it outside the table's
 * stacking context - so once it is open it paints over everything, including
 * the frozen column its own row has just slid beneath. Nothing the table can
 * set will make a portal on `<body>` pass under a cell inside the table.
 *
 * Rather than fight that, the menu closes: it is anchored to a row, and once
 * that row has moved the menu no longer points at anything. Capture phase,
 * because the scroll that matters is the table body's own and scroll events do
 * not bubble.
 */
export const subscribeToScrollDismissal = (
  close: () => void,
  target: Pick<Document, "addEventListener" | "removeEventListener"> = document
) => {
  // Capture phase is the whole point: the scroll that matters is the table
  // body's own, and scroll does not bubble - a listener on window never sees
  // it, and the menu goes on floating over the frozen column. Moving this to
  // the bubble phase looks equivalent and silently restores the bug, which is
  // why it is a named export with a test rather than an inline effect.
  target.addEventListener("scroll", close, true)
  return () => target.removeEventListener("scroll", close, true)
}

const useCloseOnScroll = (open: boolean, close: () => void) => {
  React.useEffect(
    () => (open ? subscribeToScrollDismissal(close) : undefined),
    [open, close]
  )
}

/** Longer than antd's 150ms hover delay, which is all this has to outlast. */
const MENU_REOPEN_GUARD_MS = 300

/**
 * The menu is portalled to `<body>`, but React still passes its clicks up to the
 * row it was opened from. On a clickable row, picking an item also ran the row's
 * own action: Deactivate opened the edit form as well.
 */
const stopMenuClicks = (menu: React.ReactNode) => (
  <div onClick={(event) => event.stopPropagation()}>{menu}</div>
)

// `app-table-action-select` keeps the cell from wrapping (appTable.css).
const ACTION_TRIGGER_CLASS =
  "app-table-action-select inline-flex h-9 w-9 flex-none items-center justify-center rounded-[5px] border-[1.4px] border-[#e2e8f0] bg-transparent text-[#0f172b] transition-colors hover:bg-[#f1f5f9] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#004af5] disabled:cursor-not-allowed disabled:opacity-50"

/**
 * The row action menu: the ellipsis button, its dropdown, and the dismissal
 * behaviour.
 *
 * Takes the same props as antd's `Dropdown` minus its child, so a caller that
 * was building the dropdown by hand can pass its existing `menu` straight
 * through. Every row gets the same button, even with a single action, so the
 * column reads as one control. The menu opens from the button's right edge, as
 * in the design system, so it stays on screen from the right-pinned column; a
 * caller's own `placement` still wins.
 *
 * It opens on hover. Clicking the button only ever opens it: antd's own click
 * trigger toggles, which would shut the menu on anyone who hovers and then
 * clicks, but without a click the keyboard could not open it at all.
 */
export const TableActionMenu = ({
  label,
  disabled,
  ...dropdown
}: Omit<DropdownProps, "children" | "open" | "onOpenChange"> & {
  label?: string
}) => {
  const [open, setOpen] = React.useState(false)
  const close = React.useCallback(() => setOpen(false), [])
  useCloseOnScroll(open, close)

  // Entering the menu starts antd's hover delay, and an item clicked inside it
  // would close the menu only for the delay to run out and open it again.
  const itemPickedAt = React.useRef(0)
  const handleOpenChange: DropdownProps["onOpenChange"] = (next, info) => {
    if (next && Date.now() - itemPickedAt.current < MENU_REOPEN_GUARD_MS) return
    if (!next && info?.source === "menu") itemPickedAt.current = Date.now()
    setOpen(next)
  }

  return (
    <Dropdown
      trigger={["hover"]}
      placement="bottomRight"
      dropdownRender={stopMenuClicks}
      {...dropdown}
      open={open}
      onOpenChange={handleOpenChange}
      disabled={disabled}
    >
      <TableActionTrigger
        label={label}
        disabled={disabled}
        onClick={() => setOpen(true)}
      />
    </Dropdown>
  )
}

/* ------------------------------------------------------- action select -- */

/**
 * The menu variant: a ready `items` array behind the ellipsis button, in the
 * Action column at the end of the row.
 */
export const TableActionSelect = ({
  items,
  label = "Select action",
  disabled,
}: {
  items: ItemType[]
  label?: string
  disabled?: boolean
}) => (
  <TableActionMenu
    menu={{ items }}
    label={label}
    disabled={disabled}
  />
)
